import React from 'react';
import { CONTENT_TYPES, getContentTypesForPlatform, PLATFORMS } from '../../utils/platformConfig';
import { useLanguage } from '../../contexts/LanguageContext';

const ContentTypeSelector = ({ platform, value, onChange, disabled = false, allowedContentTypes = null }) => {
  const { isRTL } = useLanguage();
  
  if (!platform) {
    return (
      <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <p>{isRTL ? 'يرجى اختيار المنصة أولاً' : 'Please select a platform first'}</p>
      </div>
    );
  }
  
  let contentTypes = getContentTypesForPlatform(platform);
  
  // Filter content types if allowedContentTypes is provided
  if (allowedContentTypes && Array.isArray(allowedContentTypes) && allowedContentTypes.length > 0) {
    contentTypes = contentTypes.filter(type => allowedContentTypes.includes(type.id));
  }
  
  const platformInfo = PLATFORMS[platform];

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {isRTL ? 'اختر نوع المحتوى' : 'Select Content Type'} <span className="text-red-500">*</span>
      </label>
      
      {contentTypes.length === 0 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {isRTL 
              ? 'لا توجد أنواع محتوى متاحة في الباقات النشطة للعميل لهذه المنصة. يرجى إضافة باقة تحتوي على أنواع المحتوى المطلوبة.' 
              : 'No content types available in the client\'s active packages for this platform. Please add a package with the required content types.'}
          </p>
        </div>
      ) : (
        <>
          {allowedContentTypes && allowedContentTypes.length > 0 && (
            <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                {isRTL 
                  ? `أنواع المحتوى المتاحة في باقات العميل: ${contentTypes.map(t => isRTL ? t.nameAr : t.name).join(', ')}`
                  : `Available content types from client packages: ${contentTypes.map(t => t.name).join(', ')}`}
              </p>
            </div>
          )}
          
          <p className="text-sm text-gray-500">
            {isRTL 
              ? `أنواع المحتوى المتاحة لـ ${platformInfo?.nameAr}` 
              : `Available content types for ${platformInfo?.name}`
            }
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {contentTypes.map((contentType) => {
          const isSelected = value === contentType.id;
          
          return (
            <button
              key={contentType.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(contentType.id)}
              className={`
                relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200
                ${
                  isSelected 
                    ? 'border-sky-500 bg-sky-50 shadow-sm scale-105' 
                    : 'border-gray-200 bg-white hover:border-sky-200 hover:bg-sky-50/60 hover:shadow-md'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Content Type Icon */}
              <div 
                className={`
                  w-14 h-14 rounded-xl flex items-center justify-center text-3xl mb-2 transition-all duration-200
                  ${isSelected ? 'bg-sky-100 shadow-md' : 'bg-gray-100'}
                `}
              >
                {contentType.icon}
              </div>
              
              {/* Content Type Name */}
              <span className={`text-sm font-medium text-center transition-colors ${isSelected ? 'text-sky-800 font-semibold' : 'text-gray-700'}`}>
                {isRTL ? contentType.nameAr : contentType.name}
              </span>
              
              {/* Video Badge */}
              {contentType.isVideo && (
                <span className={`mt-1 px-2 py-0.5 text-xs font-medium rounded-full transition-colors ${
                  isSelected ? 'bg-red-200 text-red-800' : 'bg-red-100 text-red-700'
                }`}>
                  {isRTL ? 'فيديو' : 'Video'}
                </span>
              )}
              
              {/* Selection Indicator */}
              {isSelected && (
                <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'} bg-sky-500 rounded-full p-1 shadow-md`}>
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
          </div>
        </>
      )}
      
      {/* Selected Content Type Description */}
      {value && CONTENT_TYPES[value] && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 text-sm text-gray-600">
          <span>{CONTENT_TYPES[value]?.icon}</span>
          <span>
            {isRTL ? CONTENT_TYPES[value]?.descriptionAr : CONTENT_TYPES[value]?.description}
          </span>
        </div>
      )}
    </div>
  );
};

export default ContentTypeSelector;







